import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { getAllGolfCourses } from '@/api/golfCourse/golfCourse.actions.ts';
import { GolfCourse } from '@/api/golfCourse/golfCourse.types.ts';
import {
  RewardTierStatuses,
  RewardTierTypes,
} from '@/api/rewardTier/rewardTier.types.ts';
import { isApiSuccess } from '@/api/shared.types.ts';
import {
  createVoucherBatch,
  getVoucherPaymentPayload,
} from '@/api/voucher/voucher.actions';
import {
  CreateVoucherBatchFormData,
  VoucherInfo,
} from '@/api/voucher/voucher.types';
import { useRewardTiers } from '@/hooks/useRewardTiers';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const usePurchaseVoucher = () => {
  const navigate = useNavigate();

  const [golfCourses, setGolfCourses] = useState<GolfCourse[]>([]);
  const { rewardTiers, isFetchingAll: isFetchingTiers } = useRewardTiers();
  const [selectedGolfCourse, setSelectedGolfCourse] =
    useState<GolfCourse | null>(null);

  useEffect(() => {
    const fetchCourses = async (): Promise<void> => {
      const response = await getAllGolfCourses();
      if (isApiSuccess(response)) {
        setGolfCourses(response.content.golfCourses);
      }
    };

    fetchCourses();
  }, []);

  const displayedRewardTiers = useMemo(() => {
    const baseFiltered =
      rewardTiers?.filter(
        (tier) =>
          tier.status === RewardTierStatuses.ACTIVE &&
          tier.type === RewardTierTypes.CORPORATE
      ) || [];

    if (!selectedGolfCourse) {
      return baseFiltered;
    }

    return baseFiltered.filter(
      (tier) => tier.parThree === selectedGolfCourse.parThree
    );
  }, [rewardTiers, selectedGolfCourse]);

  const [showConfirmQtyModal, setShowConfirmQtyModal] = useState(false);
  const [confirmQtyText, setConfirmQtyText] = useState('');
  const [voucherBatchData, setVoucherBatchData] =
    useState<CreateVoucherBatchFormData | null>(null);
  const [createdVouchers, setCreatedVouchers] = useState<VoucherInfo[]>([]);

  const isLoading = isFetchingTiers;

  const handleGolfCourseChange = (golfCourseId: string): void => {
    const golfCourse =
      golfCourses?.find((gc) => gc.id === golfCourseId) ?? null;
    setSelectedGolfCourse(golfCourse);
  };

  const handleSubmit = (data: CreateVoucherBatchFormData): void => {
    setVoucherBatchData(data);
    setConfirmQtyText(
      `Confirm the purchase of ${data.voucherCount} voucher(s) to continue.`
    );
    setShowConfirmQtyModal(true);
  };

  const handleConfirm = async (): Promise<void> => {
    const processCreating = async (): Promise<VoucherInfo[]> => {
      if (createdVouchers.length) {
        return createdVouchers;
      }

      const result = await createVoucherBatch(voucherBatchData!);
      if (!isApiSuccess(result)) {
        toast.error(result.error.message || 'Unable to create vouchers.');
        return [];
      }

      setCreatedVouchers(result.content.vouchers);
      return result.content.vouchers;
    };

    const processPayment = async (vouchers: VoucherInfo[]): Promise<void> => {
      const paymentResult = await getVoucherPaymentPayload(
        vouchers[0].batchId,
        vouchers[0].golfCourses[0].country
      );

      if (!isApiSuccess(paymentResult)) {
        toast.error(paymentResult.error.message || 'Vouchers payment failed');
        return;
      }

      navigate('/payment', {
        state: { redirectUrl: paymentResult.content.paymentPayload.content },
      });
    };

    setShowConfirmQtyModal(false);
    const vouchers = await processCreating();
    if (vouchers.length && vouchers[0].golfCourses.length) {
      await processPayment(vouchers);
    }
  };

  const handleCancel = (): void => {
    setShowConfirmQtyModal(false);
    setConfirmQtyText('');
    setVoucherBatchData(null);
  };

  return {
    golfCourses,
    rewardTiers: displayedRewardTiers,
    isLoading,
    showConfirmQtyModal,
    confirmQtyText,
    handleSubmit,
    handleConfirm,
    handleCancel,
    handleGolfCourseChange,
  };
};
